import React, {useEffect, useState} from 'react';
import _ from "lodash";
import {
    getAllPrintfulProductsPrices, updateSinglePrintfulProductsPrice
} from "../../services/printful-products-price-data.service";
import ShowProgressSpinner from "../../../common/ui/show-progress-spinner";
import ShowEmptyListMessage from "../../../design/components/common/show-empty-list-message";
import {
    DEFAULT_PRINTFUL_PRODUCTS_PRICE_DISCOUNT_PERCENTAGE,
    DEFAULT_PRINTFUL_PRODUCTS_PRICE_PROFIT_PERCENTAGE,
    PRINTFUL_PRODUCTS_PRICE_FIELDS_LIST, PRINTFUL_PRODUCTS_PRICE_IDS
} from "../../constants/printful-products-price.constant";
import {showDateTime} from "../../utils/printful-util";
import PrintfulProductsPriceFilters from "./printful-products-price-filters";
import EditPrintfulProductsPrice from "./edit-printful-products-price";
import {calculateProductPrice, prepareVariantsPriceRange} from "../../utils/printful-price-util";
import {getSinglePrintfulInfo, updateSinglePrintfulInfo} from "../../services/printful-info-data.service";
import {PRINTFUL_INFO_IDS} from "../../constants/printful-info.constant";
import {
    addSingleShopifyProductPriceSync, searchShopifyProductPriceSyncsWithSearchCriteria,
    updateSingleShopifyProductPriceSync
} from '../../../shopify/services/shopify-product-price-sync-data.service';
import {prepareShopifyProductPriceList} from "../../../shopify/utils/bulk-product-price-util";


const defaultFilterCriteria: any = {};

const PrintfulProductsPricesList = () => {
    const [filteredPrintfulProductsPricesList, setFilteredPrintfulProductsPricesList] = useState(null);
    const [printfulProductsPricesList, setPrintfulProductsPricesList] = useState([]);
    const [printfulProductVariantsPricesList, setPrintfulProductVariantsPricesList] = useState(null);
    const [filterCriteria, setFilterCriteria] = useState(defaultFilterCriteria);
    const [dataFetchInProgress, setDataFetchInProgress] = useState(true);
    const [initialStep, setInitialStep] = useState(true);
    const [printfulProductsPriceInfo, setPrintfulProductsPriceInfo] = useState(null);
    const [selectedPrintfulProduct, setSelectedPrintfulProduct] = useState(null);
    const [shopifyProductPriceSync, setShopifyProductPriceSync] = useState(null);
    const [syncUpdateInProgress, setSyncUpdateInProgress] = useState(false);

    useEffect(() => {
        if (initialStep) {
            setInitialStep(false);
            getPrintfulProductsPriceList();
            getCurrentShopifyProductPriceSync();
        }
    }, [initialStep]);

    const getPrintfulProductsPriceList = async () => {
        setDataFetchInProgress(true);
        let printfulProductsPriceListResult = await getAllPrintfulProductsPrices();

        let printfulProductsPriceInfoResult = await getSinglePrintfulInfo(PRINTFUL_INFO_IDS.PRINTFUL_PRODUCTS_PRICE);

        prepareProductVariantsPriceList(printfulProductsPriceListResult, printfulProductsPriceInfoResult);
        printfulProductsPriceListResult = _.orderBy(printfulProductsPriceListResult, ['product_type', 'category', 'title'], ['asc', 'asc', 'asc']);

        setPrintfulProductsPricesList(printfulProductsPriceListResult);
        applyFilter({...filterCriteria}, printfulProductsPriceListResult);
        setPrintfulProductsPriceInfo(printfulProductsPriceInfoResult);
        setDataFetchInProgress(false);
    }

    const prepareProductVariantsPriceList = (printfulProductsPriceListResult, printfulProductsPriceInfoResult) => {
        let profitPercentage = DEFAULT_PRINTFUL_PRODUCTS_PRICE_PROFIT_PERCENTAGE;
        let discountPercentage = DEFAULT_PRINTFUL_PRODUCTS_PRICE_DISCOUNT_PERCENTAGE;
        let existingProducts = printfulProductsPriceInfoResult.products || {};

        _.forEach(printfulProductsPriceListResult, (printfulProduct) => {
            let existingProduct = existingProducts[printfulProduct.product_id];

            if (existingProduct) {
                printfulProduct.profitPercentage = existingProduct.profitPercentage;
                printfulProduct.discountPercentage = existingProduct.discountPercentage;

                if (existingProduct.sync) {
                    printfulProduct.sync = existingProduct.sync;
                } else {
                    delete printfulProduct.sync;
                }
            }

            if (!_.isNumber(printfulProduct.profitPercentage)) {
                printfulProduct.profitPercentage = profitPercentage;
            }

            if (!_.isNumber(printfulProduct.discountPercentage)) {
                printfulProduct.discountPercentage = discountPercentage;
            }

            _.forEach(printfulProduct.variants, (variant) => {
                calculateProductPrice(variant, printfulProduct);
            });

            existingProduct = prepareVariantsPriceRange(printfulProduct.variants, printfulProduct, existingProduct);
            existingProducts[printfulProduct.product_id] = existingProduct;
        });

        printfulProductsPriceInfoResult.products = existingProducts;
        printfulProductsPriceInfoResult.totalProducts = _.keys(existingProducts).length;

        if (!printfulProductsPriceInfoResult.id) {
            printfulProductsPriceInfoResult.id = PRINTFUL_INFO_IDS.PRINTFUL_PRODUCTS_PRICE;
        }

    }

    useEffect(() => {
        applyFilter(filterCriteria, [...printfulProductsPricesList]);
    }, [filterCriteria]);

    useEffect(() => {
        if (printfulProductsPriceInfo) {
            savePrintfulProductsPriceInfo();
        }
    }, [printfulProductsPriceInfo]);

    const savePrintfulProductsPriceInfo = async () => {
        await updateSinglePrintfulInfo(printfulProductsPriceInfo);
    }

    const getCurrentShopifyProductPriceSync = async () => {
        let currentShopifyProductPriceSync;
        const searchCriteriaList = [];
        searchCriteriaList.push({field: 'status', condition: '==', value: 'NEW'});

        let shopifyProductPriceSyncResultList = await searchShopifyProductPriceSyncsWithSearchCriteria(searchCriteriaList);

        if (_.isEmpty(shopifyProductPriceSyncResultList)) {
            const newShopifyProductPriceSync: any = {
                status: 'NEW',
            }

            const newShopifyProductPriceSyncResult: any = await addSingleShopifyProductPriceSync(newShopifyProductPriceSync);
            newShopifyProductPriceSync.id = newShopifyProductPriceSyncResult.id;
            currentShopifyProductPriceSync = newShopifyProductPriceSync;
        } else {
            currentShopifyProductPriceSync = shopifyProductPriceSyncResultList[0];
        }

        setShopifyProductPriceSync(currentShopifyProductPriceSync);
    }

    const updateShopifyProductPriceSync = async () => {
        setSyncUpdateInProgress(true);
        const existingShopifyProductPriceSync = {...shopifyProductPriceSync};
        const existingPrintfulProductsPriceInfo = {...printfulProductsPriceInfo};
        const existingPrintfulProductsPricesList = [...printfulProductsPricesList];

        existingShopifyProductPriceSync.productPrices = prepareShopifyProductPriceList(existingPrintfulProductsPriceInfo, existingPrintfulProductsPricesList, existingShopifyProductPriceSync.productPrices);
        existingShopifyProductPriceSync.total = (_.keys(existingShopifyProductPriceSync.productPrices)).length;
        await updateSingleShopifyProductPriceSync(existingShopifyProductPriceSync);

        setShopifyProductPriceSync(existingShopifyProductPriceSync);
        setPrintfulProductsPriceInfo(existingPrintfulProductsPriceInfo);
        setPrintfulProductsPricesList(existingPrintfulProductsPricesList)
        setSyncUpdateInProgress(false);
    }


    const applyFilter = (newFilterCriteria, allPrintfulProductsPricesListResult) => {
        if (!_.isEmpty(newFilterCriteria) && !_.isEmpty(newFilterCriteria?.product_type || newFilterCriteria?.category || newFilterCriteria?.sync)) {

            if (newFilterCriteria?.sync === 'all' || newFilterCriteria?.sync === '') {
                delete newFilterCriteria?.sync;
            }

            if (newFilterCriteria?.product_type === '') {
                delete newFilterCriteria?.product_type;
            }

            if (newFilterCriteria?.category === '') {
                delete newFilterCriteria?.category;
            }

            const filteredPrintfulProductsPricesList = _.filter(allPrintfulProductsPricesListResult, newFilterCriteria);
            setFilteredPrintfulProductsPricesList(filteredPrintfulProductsPricesList);
        } else {
            setFilteredPrintfulProductsPricesList(allPrintfulProductsPricesListResult);
        }
    }

    const refreshPrintfulProductsPriceList = async () => {
        await getPrintfulProductsPriceList();
    }

    const afterPrintfulProductsPriceChange = async (editedPrintfulProduct, existingProduct) => {
        const existingPrintfulProductsPricesList = [...printfulProductsPricesList];
        const existingPrintfulProductIndex = _.findIndex(existingPrintfulProductsPricesList, {id: editedPrintfulProduct.id});
        existingPrintfulProductsPricesList[existingPrintfulProductIndex] = editedPrintfulProduct;

        const existingAllPrintfulProductsPrice = {...printfulProductsPriceInfo};
        existingAllPrintfulProductsPrice.products[editedPrintfulProduct.product_id] = existingProduct;
        setPrintfulProductsPriceInfo(existingAllPrintfulProductsPrice);

        setPrintfulProductsPricesList(existingPrintfulProductsPricesList);
        applyFilter({...filterCriteria}, existingPrintfulProductsPricesList);
    }

    const addToSync = async (printfulProduct, existingProduct) => {
        setSelectedPrintfulProduct(printfulProduct);
        existingProduct.sync = 'added';
        printfulProduct.sync = 'added';

        await updateSinglePrintfulProductsPrice(printfulProduct);
        await afterPrintfulProductsPriceChange(printfulProduct, existingProduct);
        setSelectedPrintfulProduct(null);
    }

    const removeFromSync = async (printfulProduct, existingProduct) => {
        setSelectedPrintfulProduct(printfulProduct);
        delete existingProduct.sync;
        delete printfulProduct.sync;

        await updateSinglePrintfulProductsPrice(printfulProduct);
        await afterPrintfulProductsPriceChange(printfulProduct, existingProduct);
        setSelectedPrintfulProduct(null);
    }

    const removeAllFromSync = async () => {
        const existingPrintfulProductsPricesList = [...printfulProductsPricesList];
        const existingPrintfulProductsPriceInfo = {...printfulProductsPriceInfo};

        _.forEach(existingPrintfulProductsPricesList, (printfulProduct) => {
            const existingProduct = existingPrintfulProductsPriceInfo.products[printfulProduct.product_id];
            delete existingProduct.sync;
            delete printfulProduct.sync;
        });

        setPrintfulProductsPriceInfo(existingPrintfulProductsPriceInfo);
        setPrintfulProductsPricesList(existingPrintfulProductsPricesList);
        applyFilter({...filterCriteria}, existingPrintfulProductsPricesList);
    }

    return (
        <>

            <div className="flex justify-between">

                <div className="flex justify-start items-center -ml-2 mb-2 w-full">
                    <div className={'text-xl ml-2 mr-4'}>
                        Printful Products Price

                        {filteredPrintfulProductsPricesList &&
                            <span className={'ml-2'}>({filteredPrintfulProductsPricesList?.length})</span>
                        }
                    </div>

                    {printfulProductsPricesList?.length > 0 &&
                        <PrintfulProductsPriceFilters
                            printfulProductsPricesList={printfulProductsPricesList}
                            filterCriteria={filterCriteria}
                            setFilterCriteria={setFilterCriteria}/>
                    }

                    <div className="ml-4">
                        <ShowProgressSpinner dataFetchInProgress={dataFetchInProgress}/>
                    </div>
                </div>

                <div className="text-center w-1/3 flex items-center justify-end">
                    <div className="mr-2">
                        <button
                            className="button-red-outline px-4 py-2 flex items-center justify-center"
                            onClick={removeAllFromSync}>
                            <span>Remove All From Sync</span>
                            <span className="ml-1">
                                <ShowProgressSpinner
                                    sizeClass={'w-5 h-5'}
                                    dataFetchInProgress={syncUpdateInProgress}/>
                            </span>
                        </button>
                    </div>

                    <div className="">
                        <button
                            className="button-green px-4 py-2 flex items-center justify-center"
                            onClick={updateShopifyProductPriceSync}>
                            <span>Add To Shopify Products Price Sync</span>
                            <span className="ml-1">
                                <ShowProgressSpinner
                                    sizeClass={'w-5 h-5'}
                                    dataFetchInProgress={syncUpdateInProgress}/>
                            </span>
                        </button>
                    </div>
                </div>

            </div>

            <div className="flex justify-start">

                <div
                    className="w-full bg-white mt-2">

                    <ShowEmptyListMessage
                        dataList={filteredPrintfulProductsPricesList}
                        dataFetchInProgress={dataFetchInProgress}
                        label={'Printful Products Prices'}/>

                    {filteredPrintfulProductsPricesList?.length > 0 &&
                        <div
                            className={'table-container-with-filter overflow-y-scroll border-y border-gray-400 bg-white'}>
                            <table className="w-full">
                                <thead className="">
                                <tr>
                                    <th className="p-2 border border-gray-400 bg-gray-400 sticky top-0 table-header-label w-12">S.No</th>
                                    {PRINTFUL_PRODUCTS_PRICE_FIELDS_LIST?.map((printfulProductsPriceField, rowIndex) => (
                                        <th key={`th-${rowIndex}`}
                                            className={`p-2 border border-gray-400 bg-gray-400 sticky top-0 table-header-label text-left pl-2 ${printfulProductsPriceField?.cssClass}`}>{printfulProductsPriceField?.label}</th>
                                    ))}
                                    <th className="p-2 border border-gray-400 bg-gray-400 sticky top-0 table-header-label">
                                        Actions
                                    </th>
                                </tr>
                                </thead>
                                <tbody className="">
                                {filteredPrintfulProductsPricesList?.map((printfulProductsPrice, rowIndex) => (
                                    <tr key={`row-${rowIndex}`}
                                        className={`border border-gray-400 ${printfulProductsPrice?.sync ? 'bg-purple-100' : ''}`}>
                                        <td className="p-2 border border-gray-400 m-auto">
                                            <div className="flex justify-center w-12">
                                                {rowIndex + 1}
                                            </div>
                                        </td>

                                        {PRINTFUL_PRODUCTS_PRICE_FIELDS_LIST?.map((printfulProductsPriceField, columnIndex) => (
                                            <React.Fragment key={`column-${rowIndex}-${columnIndex}`}>
                                                <td className={`p-2 border border-gray-400 m-auto ${printfulProductsPriceField?.cssClass}`}>
                                                    {printfulProductsPriceField?.optionsMap &&
                                                        <>
                                                            {printfulProductsPriceField?.optionsMap[printfulProductsPrice[printfulProductsPriceField?.attribute]]?.name}
                                                        </>
                                                    }

                                                    {!printfulProductsPriceField?.optionsMap &&
                                                        <>
                                                            {!printfulProductsPriceField?.isDate &&
                                                                <>
                                                                    {printfulProductsPriceField?.prefix &&
                                                                        <>{printfulProductsPriceField?.prefix} </>
                                                                    }

                                                                    {printfulProductsPriceField?.isTitle &&
                                                                        <>
                                                                            <div>
                                                                                {printfulProductsPrice[printfulProductsPriceField?.attribute]}
                                                                            </div>

                                                                            <div>
                                                                                {printfulProductsPriceField?.otherAttributes.map((otherAttribute, otherAttributeIndex) => (
                                                                                    <span
                                                                                        className={'text-gray-500 font-normal'}
                                                                                        key={`other-attribute-${otherAttributeIndex}`}>
                                                                                        {otherAttributeIndex !== 0 &&
                                                                                            <span
                                                                                                className={'text-gray-400'}> | </span>
                                                                                        }

                                                                                        {printfulProductsPrice[otherAttribute]}
                                                                                    </span>
                                                                                ))}
                                                                            </div>

                                                                        </>
                                                                    }

                                                                    {!printfulProductsPriceField?.isTitle &&
                                                                        <>
                                                                            {printfulProductsPrice[printfulProductsPriceField?.attribute]}
                                                                        </>
                                                                    }

                                                                    {printfulProductsPriceField?.suffix &&
                                                                        <> {printfulProductsPriceField?.suffix}</>
                                                                    }
                                                                </>
                                                            }

                                                            {printfulProductsPriceField?.isDate &&
                                                                <>
                                                                    {showDateTime(printfulProductsPrice[printfulProductsPriceField?.attribute])}
                                                                </>
                                                            }

                                                            {printfulProductsPriceField?.secondaryAttribute &&
                                                                <div className={'text-gray-500 text-sm'}>
                                                                    {printfulProductsPrice[printfulProductsPriceField?.secondaryAttribute]}
                                                                </div>
                                                            }
                                                        </>
                                                    }
                                                </td>
                                            </React.Fragment>
                                        ))}

                                        <td className="p-2 border border-gray-400 w-60">
                                            <div className="flex justify-center w-full">
                                                <EditPrintfulProductsPrice
                                                    afterChanges={afterPrintfulProductsPriceChange}
                                                    existingProduct={printfulProductsPriceInfo?.products[printfulProductsPrice?.id]}
                                                    isEditPrintfulProductsPrice={true}
                                                    selectedPrintfulProductsPrice={printfulProductsPrice}
                                                    refreshPrintfulProductsPriceList={refreshPrintfulProductsPriceList}/>

                                                <div className={'ml-2'}>
                                                    {!printfulProductsPrice?.sync &&
                                                        <button
                                                            className="button-green-outline px-3 py-1 flex justify-center items-center"
                                                            onClick={() => addToSync(printfulProductsPrice, printfulProductsPriceInfo?.products[printfulProductsPrice?.id])}>
                                                            Add To Sync

                                                            {selectedPrintfulProduct?.id === printfulProductsPrice?.id &&
                                                                <span className="ml-1">
                                                                    <ShowProgressSpinner
                                                                        sizeClass={'w-5 h-5'}
                                                                        dataFetchInProgress={true}/>
                                                                </span>
                                                            }
                                                        </button>
                                                    }

                                                    {printfulProductsPrice?.sync &&
                                                        <button
                                                            className="button-red-outline px-3 py-1 flex justify-center items-center"
                                                            onClick={() => removeFromSync(printfulProductsPrice, printfulProductsPriceInfo?.products[printfulProductsPrice?.id])}>
                                                            Remove Sync

                                                            {selectedPrintfulProduct?.id === printfulProductsPrice?.id &&
                                                                <span className="ml-1">
                                                                    <ShowProgressSpinner
                                                                        sizeClass={'w-5 h-5'}
                                                                        dataFetchInProgress={true}/>
                                                                </span>
                                                            }
                                                        </button>
                                                    }
                                                </div>
                                            </div>
                                        </td>

                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    }

                    {/*<PrintfulProductsVariantsPricesList
                        printfulProductVariantsPricesList={printfulProductVariantsPricesList}/>*/}
                </div>

            </div>

        </>
    )
}

export default PrintfulProductsPricesList
